import React, { useState, useEffect, useMemo } from "react";
import "./ClientContacts.css";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import EditClientContacts from "./EditClientContacts";

const ClientContacts = ({
  isOpen,
  onClose,
  contacts,
  clientName,
  onContactUpdated,
  clientId,
}) => {
  const [isEditContactOpen, setEditContactOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [localContacts, setLocalContacts] = useState([]);

  // Sincronizează lista locală de contacte cu datele primite din componenta părinte.
  // Astfel, când props.contacts se schimbă, gridul afișează automat noile valori.
  useEffect(() => {
    setLocalContacts(Array.isArray(contacts) ? contacts : []);
  }, [contacts]);

  // Deschide dialogul de editare și setează contactul selectat pentru modificare.
  const handleEditContact = (contact) => {
    setContactToEdit(contact);
    setEditContactOpen(true);
  };

  // Închide dialogul de editare și curăță contactul selectat.
  const handleCloseEditContact = () => {
    setEditContactOpen(false);
    setContactToEdit(null);
  };

  // Se apelează după editarea cu succes a unui contact.
  // Actualizează lista locală, notifică părintele dacă este nevoie
  // și închide fereastra de editare.
  const handleEditSuccess = (updatedContact) => {
    setLocalContacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact.id === updatedContact.id ? updatedContact : contact
      )
    );

    if (onContactUpdated) {
      onContactUpdated();
    }

    handleCloseEditContact();
  };

  // Configurarea coloanelor pentru tabelul DataGrid.
  // Include câmpurile standard și o coloană de acțiuni pentru editare.
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        minWidth: 150,
      },
      {
        field: "position",
        headerName: "Position",
        flex: 1.5,
        minWidth: 130,
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 1.5,
        minWidth: 130,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 2.5,
        minWidth: 180,
      },
      {
        field: "contact_status",
        headerName: "Status",
        flex: 1,
        minWidth: 100,

        // Normalizează valoarea statusului pentru afișare și sortare.
        // Dacă lipseste din backend, consideră implicit "Active".
        valueGetter: (value, row) => row?.contact_status || "Active",

        // Afișează statusul contactului sub formă de badge stilizat.
        renderCell: (params) => {
          const status = params?.row?.contact_status || "Active";
          const isActive = status === "Active";

          return (
            <span
              className={`client-contacts-modal__status-badge ${
                isActive
                  ? "client-contacts-modal__status-badge--active"
                  : "client-contacts-modal__status-badge--inactive"
              }`}
            >
              {status}
            </span>
          );
        },

        // Comparator custom pentru sortare:
        // contactele inactive sunt împinse după cele active.
        // Nu mai folosim param1.row și param2.row, fiindcă în unele versiuni
        // de MUI DataGrid pot fi undefined și apare eroarea:
        // "can't access property contact_status, param1.row is undefined".
        sortComparator: (v1, v2) => {
          const status1 = v1 || "Active";
          const status2 = v2 || "Active";

          if (status1 === "Inactive" && status2 !== "Inactive") return 1;
          if (status1 !== "Inactive" && status2 === "Inactive") return -1;
          return status1.localeCompare(status2);
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.7,
        minWidth: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",

        // Butonul de editare pentru fiecare rând din tabel.
        // Deschide dialogul de editare pentru contactul curent.
        renderCell: (params) => (
          <Button
            variant="text"
            onClick={() => handleEditContact(params.row)}
            size="small"
            sx={{ minWidth: "auto", padding: "8px" }}
            title="Edit contact"
          >
            <EditIcon fontSize="small" color="primary" />
          </Button>
        ),
      },
    ],
    []
  );

  // Dacă modalul nu este deschis, componenta nu randă nimic.
  if (!isOpen) return null;

  return (
    <>
      {/* Modalul principal care afișează lista contactelor */}
      {!isEditContactOpen && (
        <div className="client-contacts-modal">
          <div className="client-contacts-modal__overlay">
            <div className="client-contacts-modal__popup">
              <div className="client-contacts-modal__header">
                <h2 className="client-contacts-modal__title">
                  Contact Persons - {clientName}
                </h2>
              </div>

              <div className="client-contacts-modal__content">
                {/* Dacă există contacte, se afișează tabelul. */}
                {localContacts && localContacts.length > 0 ? (
                  <Box sx={{ height: "100%", width: "100%" }}>
                    <DataGrid
                      rows={localContacts}
                      columns={columns}
                      getRowId={(row) =>
                        row?.id ??
                        `${row?.email || "no-email"}-${row?.phone || "no-phone"}`
                      }
                      getRowHeight={() => "auto"}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10 },
                        },
                        sorting: {
                          sortModel: [{ field: "contact_status", sort: "asc" }],
                        },
                      }}
                      pageSizeOptions={[5, 10, 20, 25]}
                      disableRowSelectionOnClick
                      sx={{
                        border: "none",
                        "& .MuiDataGrid-cell": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: "1.4",
                          display: "flex",
                          alignItems: "center",
                          paddingTop: "8px",
                          paddingBottom: "8px",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#f8f9fa",
                          borderBottom: "2px solid #dee2e6",
                          color: "#495057",
                          fontWeight: 600,
                        },
                        "& .MuiDataGrid-cell:focus": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                          outline: "none",
                        },
                        "& .Mui-selected": {
                          backgroundColor: "transparent !important",
                        },
                        "& .Mui-selected:hover": {
                          backgroundColor: "transparent !important",
                        },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "& .MuiDataGrid-footerContainer": {
                          borderTop: "1px solid #e0e0e0",
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <p className="client-contacts-modal__empty">
                    No contact persons found for this client.
                  </p>
                )}
              </div>

              {/* Footer-ul modalului principal */}
              <div className="client-contacts-modal__footer">
                <button
                  type="button"
                  className="client-contacts-modal__button-close"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialogul de editare contact.
          Este afișat doar când utilizatorul a ales un contact pentru editare. */}
      {isEditContactOpen && (
        <EditClientContacts
          isOpen={isEditContactOpen}
          onClose={handleCloseEditContact}
          contact={contactToEdit}
          clientId={clientId}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
};

export default ClientContacts;